// frontend/src/features/agency/ActiveDriverList.js
// Phase 8.7C: Agency "Active" pool.
//
// Key principle:
// - Membership comes from the backend read model: GET /agencies/me/drivers/active
// - This component does NOT subscribe to socket events anymore.
//   Real-time invalidation + stats bump are owned by services/socket.js (Option A/2).
//
// The list still reloads after *manual* actions (create/reserve/delete) so the UI
// updates immediately for the user who performed the action.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { upsertDrivers, upsertDriver, removeDriver } from '../../features/drivers/driverSlice';

import { fetchAgencyActiveDrivers, addDriver } from '../../services/driverService';

import axios from 'axios';
import DriverForm from '../../features/drivers/DriverForm';
import DriverEditModal from '../../features/drivers/DriverEditModal';
import { Edit2, UserCheck, Trash2 } from 'lucide-react';
import { selectAgencyIdForUser } from '../auth/authSlice';
import { selectQuery, selectStatsRefreshKey } from '../ui/filtersSlice';

function filterRowsByQuery(rows, q) {
  const needle = (q ?? '').trim().toLowerCase();
  if (!needle) return rows || [];

  return (rows || []).filter((row) => {
    const haystacks = [
      row?.name,
      row?.vehicle_registration_number,
      row?.assignment_status,
      row?.assignment_id != null ? String(row.assignment_id) : null,
      row?.ban_reason,
      row?.reason,
      row?.ban_scope,
      row?.ban_site_id != null ? String(row.ban_site_id) : null,
      row?.ban_id != null ? String(row.ban_id) : null,
    ];

    return haystacks.some((v) => String(v ?? '').toLowerCase().includes(needle));
  });
}


export default function ActiveDriverList() {
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyIdForUser);
  const statsKey = useSelector(selectStatsRefreshKey);
  const q = useSelector(selectQuery);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // ✅ Prevent duplicate initial fetches for same deps (agencyId + statsKey)
  const lastFetchKeyRef = useRef(null);

  async function reload() {
    const list = await fetchAgencyActiveDrivers();
    setRows(list);
    dispatch(upsertDrivers(list)); // optional entity cache
  }

useEffect(() => {
  if (!agencyId) return;

  // ✅ Dedupe: only fetch once per {agencyId, statsKey} combo
  const fetchKey = `${agencyId}|${statsKey}`;
  if (lastFetchKeyRef.current === fetchKey) return;
  lastFetchKeyRef.current = fetchKey;

  let cancelled = false;

  (async () => {
    try {
      await reload();
    } finally {
      if (!cancelled) setLoading(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [agencyId, statsKey]);


  const filtered = useMemo(() => filterRowsByQuery(rows, q), [rows, q]);
  const sorted = useMemo(() => [...filtered], [filtered]);


  const handleCreate = async (formData) => {
    try {
      const d = await addDriver(formData);
      dispatch(upsertDriver(d));
      setShowAdd(false);

      // New drivers typically land outside "Active" in Phase 8 (often Available),
      // but we reload anyway to keep this tab correct.
      await reload();
    } catch (err) {
      console.error(err);
    }
  };

  // Legacy status flip (temporary): always refetch afterwards.
  const handleReserve = async (id) => {
    try {
      await axios.put(`/agencies/drivers/${id}`, { status: 'Reserved' });
      await reload();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      await axios.delete(`/agencies/drivers/${id}`);
      dispatch(removeDriver(id));
      await reload();
    } catch (err) {
      console.error(err);
    }
  };

return (
  <>
    {loading ? (
      <div className="flex justify-center items-center h-64">Loading…</div>
    ) : (
      <div className="my-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-gray-800">Active Drivers</h3>

          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition"
          >
            Add Driver
          </button>
        </div>

        {showAdd && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAdd(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <DriverForm
                agencyId={agencyId}
                onSave={handleCreate}
                onClose={() => setShowAdd(false)}
              />
            </div>
          </div>
        )}

        {q?.trim() && sorted.length === 0 && (
          <div className="text-sm text-gray-500 py-3">
          No matches for “{q.trim()}”.
        </div>
        )}


        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Vehicle Reg.', 'Assignment', 'Actions'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {sorted.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{row.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">{row.vehicle_registration_number}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {row.assignment_status ? `${row.assignment_status} (#${row.assignment_id})` : '—'}
                </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => setSelectedDriver(row)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>

                      <button
                        onClick={() => handleReserve(row.id)}
                        className="p-1 hover:bg-gray-100 rounded text-yellow-600"
                        title="Reserve (legacy)"
                      >
                        <UserCheck size={16} />
                      </button>

                      <button
                        onClick={() => handleDeleteClick(row.id)}
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedDriver && (
          <DriverEditModal
            driver={selectedDriver}
            onClose={() => setSelectedDriver(null)}
            onUpdate={async () => {
              setSelectedDriver(null);
              await reload();
            }}
          />
        )}
      </div>
    )}
  </>
);
}